import LinkedProductName from './LinkedProductName';
import OrderAddressText from './OrderAddressText';
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useOrderRowLayout } from '../orderRowLayout';
import type { SellerGift } from '../Interface/Order';
import { loadMySellerGifts, loadSellerGifts, sellerGiftError, sellerGiftLabel, sellerGiftSourceOrder } from '../sellerGifts';
import { OrderCell } from '../style/orderTable_style2';

const OrderGifts = createContext<{ items: SellerGift[]; unmatched: number[] } | null>(null);

export function OrderGiftRows({ orderSeq, minWidth, tableColumns, showOwner = false }: { orderSeq: number; minWidth?: number; tableColumns?: number; showOwner?: boolean }) {
  const context = useContext(OrderGifts);
  const items = context?.items.filter(gift => sellerGiftSourceOrder(gift) === orderSeq);
  if (!context || !items?.length) return null;
  const content = <GiftRows aria-label={`주문 ${orderSeq} 연계 상품`} $minWidth={minWidth}>
    {items.map(gift => <div className="gift-order-row" key={gift.seq}>
      <span className="gift-kind">{gift.cancelled_at ? '연계 취소' : '연계 상품'}</span>
      {showOwner && <span>주문 {orderSeq} · {gift.order_nickname || gift.order_name || '-'}</span>}
      <strong>{sellerGiftLabel(gift)}</strong><span>{gift.option_name ? `옵션: ${gift.option_name}` : '옵션 없음'}</span>
      <span>{gift.cancelled_at ? 0 : gift.count.toLocaleString()}개</span><span className="gift-amount">0원</span><span>{gift.cancelled_at ? '연계 구매 상품 취소' : gift.procurement_seq ? '발주 등록됨' : '배송 대상'}</span>
    </div>)}
  </GiftRows>;
  return tableColumns ? <tr><td colSpan={tableColumns} style={{ padding: 0 }}>{content}</td></tr> : content;
}


export function useOrderGiftItems() {
  return useContext(OrderGifts)?.items || [];
}

type GiftColumn = 'blank' | 'number' | 'date' | 'status' | 'product' | 'name' | 'option' | 'amount' | 'quantity' | 'recipient' | 'phone' | 'postcode' | 'address' | 'memo' | 'cancelDate';
/** Explicit bottom cells share the ordinary order's number and totals. */
export function OrderGiftGridCells({ items, firstRow, columns }: { items: SellerGift[]; firstRow: number; columns: Record<number, GiftColumn> }) {
  const layout = useOrderRowLayout();
  return <>{items.flatMap((gift, index) => Object.entries(columns).map(([column, field]) => {
    const text: Record<GiftColumn, ReactNode> = {
      cancelDate: gift.cancelled_at ? new Date(gift.cancelled_at).toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 16) : '—',
      blank: '—', number: `G-${gift.seq}`, date: gift.created_at ? new Date(gift.created_at).toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).slice(0, 16) : '—',
      status: gift.process_status && gift.process_status !== '연계 예정' ? gift.process_status : gift.cancelled_at ? '연계 취소' : gift.procurement_seq ? '발주완료' : '배송 대상', product: gift.product_num == null || String(gift.product_num).trim() === '' ? '-' : gift.product_num,
      name: <LinkedProductName name={gift.product_name} />, option: <OrderAddressText address={gift.option_name || '—'} />,
      amount: '0원', quantity: `${gift.count}개`, recipient: <OrderAddressText address={gift.addr_recipient || '—'} />, phone: <OrderAddressText address={gift.addr_phone || '—'} />, postcode: <OrderAddressText address={gift.addr_num || '—'} />,
      address: <OrderAddressText address={[gift.addr_basic, gift.addr_detail].filter(Boolean).join(' ')} />, memo: gift.cancelled_at ? '연계 구매 상품 취소·환불' : '연계 상품 · 주문과 함께 배송',
    };
    return <OrderCell key={`${gift.seq}-${column}`} className={`item${field === 'name' ? ' product-name' : ''}`} data-seller-gift={gift.seq}
      aria-label={field === 'name' ? `주문 ${sellerGiftSourceOrder(gift)} 연계 상품` : undefined}
      style={{ gridColumn: Number(column), gridRow: layout?.giftRows.get(gift.seq) ?? firstRow + index }}>
      {text[field]}
    </OrderCell>;
  }))}</>;
}

export function UnmatchedOrderGiftRows() {
  const context = useContext(OrderGifts);
  return <>{context?.unmatched.map(seq => <OrderGiftRows key={seq} orderSeq={seq} showOwner />)}</>;
}

export default function SellerGiftList({ orderSeq, liveSeq, procurementSeq, orders, refreshKey = 0, customer = false, autoRefresh = false, onLoaded, mine = false, hideWhenEmpty = false, inlineOrders = false, quietLoading = false, onLoadError, includeUnlistedOrders = false, children }: {
  orderSeq?: number; liveSeq?: number; procurementSeq?: number; orders?: { seq: number; order_nickname: string; order_name: string }[]; refreshKey?: number | string; customer?: boolean;
  onLoaded?: (items: SellerGift[]) => void; mine?: boolean; hideWhenEmpty?: boolean; autoRefresh?: boolean;
  inlineOrders?: boolean; quietLoading?: boolean;onLoadError?:(message:string)=>void; includeUnlistedOrders?: boolean; children?: ReactNode;
}) {
  const [items, setItems] = useState<SellerGift[] | null>(null), [error, setError] = useState(''), [retry, setRetry] = useState(0), [busy, setBusy] = useState(false);
  const loaded = useRef(onLoaded); loaded.current = onLoaded;
  const reportError=useRef(onLoadError);reportError.current=onLoadError;
  const [giftPage, setGiftPage] = useState(1), [giftTotal, setGiftTotal] = useState(0);
  const scope = mine ? { mine: true, page: giftPage } : Number.isSafeInteger(orderSeq) && Number(orderSeq) > 0 ? { order_seq: orderSeq }
    : Number.isSafeInteger(liveSeq) && Number(liveSeq) > 0 ? { live_seq: liveSeq, ...(procurementSeq ? { procurement_seq: procurementSeq } : {}) } : null;
  const scopeKey = JSON.stringify(scope);
  const previousScope = useRef(scopeKey);
  useEffect(() => {
    if (!scope) return;
    const controller = new AbortController();
    if (!customer || previousScope.current !== scopeKey) setItems(null);
    previousScope.current = scopeKey;
    setError(''); setBusy(true);
    const request = mine ? loadMySellerGifts(giftPage, controller.signal)
      : loadSellerGifts(JSON.parse(scopeKey), controller.signal).then(items => ({ items, total: items.length }));
    void request.then(result => {
      if (!controller.signal.aborted) { setItems(result.items); setGiftTotal(result.total); loaded.current?.(result.items);reportError.current?.(''); }
    }).catch(cause=>{if(!controller.signal.aborted){const message=sellerGiftError(cause);setError(message);reportError.current?.(message);}})
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => { controller.abort(); };
    // The serialized scope avoids reloading for unchanged order-array identities.
  }, [scopeKey, refreshKey, retry]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!customer || !autoRefresh) return;
    let lastRefresh = 0;
    const refresh = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastRefresh < 1000) return;
      lastRefresh = Date.now();
      setRetry(value => value + 1);
    };
    const interval = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [customer, autoRefresh, scopeKey]);
  const findOrder = useCallback((seq: number | null) => orders?.find(order => order.seq === seq), [orders]);
  if (!scope) return <>{children}</>;
  const visible = orders && !includeUnlistedOrders ? items?.filter(gift => gift.order_seq == null || !!findOrder(gift.order_seq)) : items;
  if (inlineOrders && !customer) {
    const unmatched = [...new Set((visible || []).filter(gift => !findOrder(sellerGiftSourceOrder(gift) ?? null)).map(gift => Number(sellerGiftSourceOrder(gift))))];
    return <OrderGifts.Provider value={{ items: visible || [], unmatched }}>
      {((busy && !quietLoading) || (error&&!onLoadError)) && <GiftToolbar>
        {busy && !quietLoading && <span role="status">연계 내역을 불러오는 중입니다.</span>}
        {error && <span role="alert">{error}</span>}
        {error && <button type="button" disabled={busy} onClick={() => setRetry(value => value + 1)}>연계 내역 다시 불러오기</button>}
      </GiftToolbar>}
      {children}

    </OrderGifts.Provider>;
  }
  if (hideWhenEmpty && !busy && !error && visible?.length === 0) return null;
  return <Section $customer={customer} aria-label="연계 상품 내역">
    <div className="gift-header"><h3>연계 상품 내역</h3>{!customer && <button type="button" aria-label="연계 내역 새로고침" disabled={busy} onClick={() => setRetry(value => value + 1)}>새로고침</button>}</div>
    {busy && !visible?.length && <p role="status">연계 내역을 불러오는 중입니다.</p>}
    {error && <p role="status">{error} <button type="button" disabled={busy} onClick={() => setRetry(value => value + 1)}>연계 내역 다시 불러오기</button></p>}
    {visible && !visible.length && <p>연계 내역이 없습니다.</p>}
    {!!visible?.length && <ul>{visible.map(gift => <li key={gift.seq}>
      {!customer && <span>{gift.order_nickname || findOrder(gift.order_seq)?.order_nickname || gift.order_name || findOrder(gift.order_seq)?.order_name || `주문 #${sellerGiftSourceOrder(gift)}`} · </span>}
      <strong>{sellerGiftLabel(gift)}</strong>{gift.option_name && <small>옵션: {gift.option_name}</small>}
      {gift.source_product_name && <small>기준 구매 상품: {gift.source_product_name}</small>}
      {gift.cancelled_at && <small>연계 취소 · 연계 구매 상품 취소·환불</small>}
      {!customer && !gift.cancelled_at && <small>{gift.procurement_seq ? '발주 등록됨' : '배송 대상'}</small>}
    </li>)}</ul>}
    {mine && giftTotal > 20 && <nav aria-label="연계 내역 페이지"><button type="button" disabled={busy || giftPage === 1} onClick={() => setGiftPage(value => value - 1)}>이전 연계 내역</button><span>{giftPage} / {Math.ceil(giftTotal / 20)}</span><button type="button" disabled={busy || giftPage * 20 >= giftTotal} onClick={() => setGiftPage(value => value + 1)}>다음 연계 내역</button></nav>}
  </Section>;
}
const Section = styled.section<{ $customer: boolean }>`
  margin: 12px 0; padding: 16px; border: 1px solid ${p => p.$customer ? 'var(--user-border, #e5e7eb)' : 'var(--color-border-default)'};
  border-radius: 12px; background: ${p => p.$customer ? 'var(--user-surface)' : 'var(--color-bg-surface)'};
  color: ${p => p.$customer ? 'var(--user-text)' : 'var(--color-text-primary)'};
  h3 { margin: 0 0 10px; font-size: 15px; } p { margin: 6px 0; font-size: 13px; }
  .gift-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; } .gift-header h3 { margin: 0; }
  ul { margin: 0; padding-left: 20px; } li { margin: 8px 0; font-size: 14px; } small { display: block; font-size: 12px; opacity: .8; }
  button { padding: 6px 10px; color: inherit; background: transparent; border: 1px solid currentColor; border-radius: 6px; font: inherit; cursor: pointer; }
`;

const GiftRows = styled.div<{ $minWidth?: number }>`
  min-width: ${p => p.$minWidth ? `${p.$minWidth}rem` : '0'};
  color: var(--color-text-primary); background: var(--color-bg-surface); font-size: var(--text-body);
  .gift-order-row { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-4); min-height: var(--control-lg); padding: var(--space-2) var(--space-4); border: 1px solid var(--color-border-table); border-top: 0; }
  .gift-kind { color: var(--color-text-secondary); font-size: var(--text-caption); }
  strong { font-weight: var(--font-weight-medium); } .gift-amount { font-variant-numeric: tabular-nums; }
`;
const GiftToolbar = styled.div`
  display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3);
  color: var(--color-text-secondary); font-size: var(--text-caption);
  button { min-height: var(--control-lg); padding: 0 var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); color: var(--color-text-primary); background: var(--color-bg-surface); font: inherit; cursor: pointer; }
  button:hover { background: var(--color-bg-subtle); } button:disabled { color: var(--color-text-disabled); cursor: default; }
  button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

